import { type ComputedRef, type Ref, computed, onMounted, onUnmounted, ref } from 'vue'
import type { IChat, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'
import {
  type ChatReadyState,
  canShowChatTimeline,
  resolveChatReadyState,
} from '@/modules/chat-matrix-fast/services/chatReadiness'
import { ChatPermissions } from '@/modules/chat-matrix-fast/services/chatPermissions'
import { ensureMatrixChatMembership, matrixJoinEpoch } from '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
import { clearMembershipStatus, membershipRevision } from '@/modules/chat-matrix-fast/services/matrixMembershipState'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { getCurrentMatrixUserId } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { useCurrentUser } from '@/modules/chat-matrix-fast/composables/useCurrentUser'

export function useChatComposerReady(options: {
  chat: Ref<IChat | null | undefined> | ComputedRef<IChat | null | undefined>
  activeTopic?: Ref<IChatTopic | null | undefined> | ComputedRef<IChatTopic | null | undefined>
  showTopicsList?: Ref<boolean> | ComputedRef<boolean>
}) {
  const matrixTick = ref(0)
  let detachSync: (() => void) | null = null
  let cancelled = false
  const { id: currentUserId } = useCurrentUser()

  const bump = () => {
    matrixTick.value += 1
  }

  onMounted(() => {
    cancelled = false
    void (async () => {
      try {
        const client = await startMatrixClient()
        if (cancelled) {
          return
        }
        const onSync = () => bump()
        client.on('sync' as any, onSync)
        detachSync = () => client.removeListener('sync' as any, onSync)
      } catch {
        // клиент ещё не готов — epoch/sync после join всё равно пересчитает
      }
    })()
  })

  onUnmounted(() => {
    cancelled = true
    detachSync?.()
    detachSync = null
  })

  const readyState = computed((): ChatReadyState => {
    void matrixTick.value
    void matrixJoinEpoch.value
    void membershipRevision.value
    return resolveChatReadyState({
      chat: options.chat.value,
      activeTopic: options.activeTopic?.value ?? null,
      showTopicsList: options.showTopicsList?.value ?? false,
    })
  })

  const canCompose = computed(() => readyState.value.canCompose)
  const canPost = computed(() =>
    ChatPermissions.canPostInChat(
      options.chat.value,
      currentUserId.value,
      getCurrentMatrixUserId(),
    ),
  )
  const showComposer = computed(() => canCompose.value && canPost.value)
  const readyMessage = computed(() => readyState.value.message)
  const readyStatus = computed(() => readyState.value.status)
  const showTimeline = computed(() => canShowChatTimeline(readyState.value.status))
  const retrying = ref(false)
  const canRetryConnection = computed(() => options.chat.value?.availability?.status !== 'unavailable'
    && options.activeTopic?.value?.availability?.status !== 'unavailable')
  const retryConnection = async () => {
    const chat = options.chat.value
    const roomId = options.activeTopic?.value?.matrix_room_id || chat?.matrix_room_id
    if (!chat || !roomId || retrying.value || !canRetryConnection.value) return
    retrying.value = true
    clearMembershipStatus(roomId, getCurrentMatrixUserId())
    try {
      await ensureMatrixChatMembership({ chatId: chat.id, roomId, chat, forceRepair: true })
    } catch {
      // The protocol stores the visible failure reason; keep the composer gated.
    } finally {
      retrying.value = false
      bump()
    }
  }

  return {
    readyState,
    canCompose,
    canPost,
    showComposer,
    readyMessage,
    readyStatus,
    showTimeline,
    retryConnection,
    retrying,
    canRetryConnection,
    bumpMatrixReady: bump,
  }
}
